import React, { useState } from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import arrayMutators from "final-form-arrays";
import { useSnackbar } from "notistack";
import { useDebouncedCallback } from "use-debounce";

import {
  hydrateLayer,
  selectDraftConfigEntities,
  selectVersionMlcId,
  useUpdateConfigAttributeMutation,
  useUpdateLayerConfigMutation,
} from "fond/api";
import { getCurrentProject } from "fond/project";
import { AdvancedSettings, LayerAttributes, LayerHeader } from "fond/styleEditor";
import { ConfigAttribute } from "fond/types";
import { LayerConfig, LayerConfigHydrated } from "fond/types/ProjectLayerConfig";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { Tab, Tabs } from "../FieldSelection/fieldSelection.styles";
import { Form } from "../StyleEditor/styleEditor.styles";
import { Container, TabPanel } from "./layerEditor.styles";

type TabType = "attributes" | "advanced";

interface IProps {
  layerConfig: LayerConfig;
}

type IFormData = {
  Attributes: ConfigAttribute[];
};

const LayerEditor: React.FC<IProps> = ({ layerConfig }: IProps) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mapLayerConfigId = useAppSelector(selectVersionMlcId);
  const defaultSystemOfMeasurement = useAppSelector((state) => getCurrentProject(state.project).SystemOfMeasurement);
  const [updateAttribute] = useUpdateConfigAttributeMutation();
  const [updateLayerConfig] = useUpdateLayerConfigMutation();
  const draft = useSelector(selectDraftConfigEntities);

  const [tabValue, setTabValue] = useState<TabType>("attributes");
  const [selectedId, setSelectedId] = useState(layerConfig.Attributes?.[0]?.ID);

  const handleOnSubmit = useDebouncedCallback((values: IFormData) => {
    const modifiedAttribute = values.Attributes.find((attribute) => attribute.ID === selectedId);
    if (modifiedAttribute) {
      updateAttribute({ attribute: modifiedAttribute, mapLayerConfigId });
    }
  }, 500);

  const handleKeyChange = useDebouncedCallback((newKey: string) => {
    const newLayer = hydrateLayer(draft, { ...layerConfig, Key: newKey }) as LayerConfigHydrated;
    updateLayerConfig({ layerConfig: newLayer, mapLayerConfigId }).catch(() => {
      enqueueSnackbar("Failed to save draft changes", { variant: "error" });
    });
  }, 500);

  return (
    <Container>
      <LayerHeader layerConfig={layerConfig} />
      <Tabs
        value={tabValue}
        onChange={(event, newValue) => {
          setTabValue(newValue);
        }}
      >
        <Tab value="attributes" label="Attributes" />
        <Tab value="advanced" label="Advanced" />
      </Tabs>
      {tabValue === "attributes" && (
        <FinalForm<IFormData>
          initialValues={{ Attributes: layerConfig.Attributes }}
          onSubmit={handleOnSubmit}
          subscription={{ submitting: true, pristine: true }}
          render={({ handleSubmit }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <TabPanel>
                  <LayerAttributes
                    attributes={layerConfig.Attributes ?? []}
                    selectedAttributeId={selectedId}
                    onSelect={setSelectedId}
                    defaultSystemOfMeasurement={defaultSystemOfMeasurement}
                  />
                </TabPanel>
                {/* We use FormSpy to submit the form values on every update */}
                <FormSpy
                  subscription={{ values: true, valid: true, pristine: true }}
                  onChange={({ valid, pristine, values }) => {
                    if (valid && !pristine) {
                      handleSubmit();
                    }
                  }}
                />
              </Form>
            );
          }}
          mutators={{ ...arrayMutators }}
        />
      )}
      {tabValue === "advanced" && <AdvancedSettings keyValue={layerConfig.Key} onKeyChange={handleKeyChange} />}
    </Container>
  );
};

export default LayerEditor;
